import { type FormEvent, type Ref } from "react";
import { Composer } from "../Composer";
import { FollowupChips } from "../FollowupChips";
import { SEED_CHIPS } from "./specHelpers";
import type { IntelGate } from "@/features/intel-layer/hooks/useIntelGate";

interface EmptyStateProps {
  clientName: string;
  draft: string;
  streaming: boolean;
  /** DB-driven chat gate. "pending" → greeting only (the check resolves in
   *  ~100ms; nothing flashes); "coming_soon" → notice in place of the tagline,
   *  composer, and starter chips; "open" → the full surface. */
  chatGate?: IntelGate;
  onDraftChange: (next: string) => void;
  onSubmit: (e: FormEvent) => void;
  onStop: () => void;
  onPickFollowup: (q: string) => void;
  /** Forwarded to the composer input so the parent can autofocus it. */
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * Empty-state agent surface content — greeting + composer + starter chips.
 *
 * The animating wrapper lives in DashboardSurface as the direct <AnimatePresence>
 * child (a native motion.div), NOT here: AnimatePresence must own the element it
 * animates, and a native motion child avoids framer-motion probing a custom
 * component's `ref` (which trips React 18.3's "ref is not a prop" warning). The
 * input ref is passed as a plain prop rather than via forwardRef.
 */
export function EmptyState({
  clientName,
  draft,
  streaming,
  chatGate = "open",
  onDraftChange,
  onSubmit,
  onStop,
  onPickFollowup,
  inputRef,
}: EmptyStateProps) {
  return (
    <>
      <div className="flex items-center gap-4">
        <span
          aria-hidden
          className="inline-block h-12 w-12 shrink-0 rounded-full border-[4px] border-primary bg-background"
        />
        <h2 className="text-4xl font-bold tracking-tight">
          Hello, {clientName || "there"}
        </h2>
      </div>

      {chatGate === "coming_soon" ? (
        <p className="mt-3 text-lg text-muted-foreground">
          This feature will be coming soon.
        </p>
      ) : chatGate === "pending" ? null : (
        <>
          <p className="mt-3 text-lg text-muted-foreground">
            How can I help you analyze your portfolio today?
          </p>

          <div className="mt-10 w-full max-w-3xl">
            <Composer
              ref={inputRef}
              placement="centered"
              draft={draft}
              onDraftChange={onDraftChange}
              onSubmit={onSubmit}
              streaming={streaming}
              onStop={onStop}
              clientName={clientName}
            />
          </div>

          <div className="mt-4 w-full max-w-3xl">
            <FollowupChips items={SEED_CHIPS} onPick={onPickFollowup} variant="grid" />
          </div>
        </>
      )}
    </>
  );
}
